<script setup>
import {computed, onMounted, ref, watch} from 'vue'
import {showSuccessMessage} from '@/helper';
import {Dialog, DialogPanel, DialogTitle, TransitionRoot} from '@headlessui/vue'
import {createConfirmDialog} from "vuejs-confirm-dialog";
import IntermediaryService from "@/services/IntermediaryService";
import router from "@/router";
import ModalDialog from "@/components/modals/ModalDialog.vue";
import {ErrorMessage, Field, Form} from 'vee-validate';
import ClientService from "@/services/ClientService";
import ContractTypeService from "@/services/ContractTypeService";
import {useI18n} from 'vue-i18n';

const {t} = useI18n()
const props = defineProps({
  contract: {type: Object, required: false},
  // ID klienta – potřeba při zakládání smlouvy pro vyřazení typů, které klient už má
  clientId: {type: [Number, String], required: false},
});

const contract = ref({contract_number: '', contract_type_id: ''})

const isOpen = ref(true)

const editing = ref(false);

const emit = defineEmits(['confirm', 'cancel']);

const closeModalWith = (value) => {
  setTimeout(() => {
    if (value === 'confirm') {
      emit('confirm', contract.value);
      isOpen.value = false;
    } else {
      emit('cancel');
      isOpen.value = false;
    }
  }, 300);
}

const contractTypes = ref([]);
const typesLoaded = ref(false);

// při zakládání a po načtení typů: klient už má všechny dostupné typy smluv
const noTypeAvailable = computed(() => !editing.value && typesLoaded.value && contractTypes.value.length === 0);

onMounted(async () => {
      getContractTypes();
    }
);

if (props.contract) {
  contract.value = { ...props.contract } ;
  editing.value = true;
}

function deleteContract() {
  console.log(contract.value);
  const {reveal, onConfirm, onCancel} = createConfirmDialog(ModalDialog, {
    title: t('createUpdateContractModal.delete_contract'),
    message: t('createUpdateContractModal.delete_contract_confirm'),
  });
  onConfirm(() => {
    ClientService.deleteContract(contract.value.client_id,contract.value.id).then(() => {
      showSuccessMessage(t('createUpdateContractModal.contract_deleted'));
      //router.push({ name: 'Intermediaries' });
    });
  });
  reveal();
}

/**
 * Načte aktivní typy smluv.
 * Klient nesmí mít dvě aktivní smlouvy stejného typu (IMA-523) – při zakládání
 * proto načteme i jeho nesmazané smlouvy a jejich typy ze seznamu vyřadíme.
 * BE stejnou podmínku kontroluje ve validaci, tady jde o to, aby uživatel neplatný
 * typ vůbec neviděl. Při editaci se typ nemění, filtr se tedy neaplikuje.
 */
async function getContractTypes() {
  const [types, clientContracts] = await Promise.all([
    ContractTypeService.getAllActive(),
    // getClientContractsv2 bez with_trashed vrací jen nesmazané (aktivní) smlouvy
    // při chybě načtení smluv nabídneme všechny typy – duplicitu stejně zastaví BE validace
    !editing.value && props.clientId
        ? ClientService.getClientContractsv2(props.clientId).catch(() => [])
        : Promise.resolve([]),
  ]);
  const usedTypeIds = new Set((Array.isArray(clientContracts) ? clientContracts : []).map(c => Number(c.contract_type_id)));
  contractTypes.value = (Array.isArray(types) ? types : []).filter(type => !usedTypeIds.has(Number(type.id)));
  typesLoaded.value = true;
}
function openModal() {
  isOpen.value = true
}
</script>

<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div"  class="modal show d-block" style="z-index: 1055;">
      <div class="modal-backdrop fade show"></div>
      <div class="modal-dialog modal-dialog-centered modal-lg" style="z-index: 1055">
        <div class="modal-content">
          <DialogPanel class="modal-body p-4 rounded-lg shadow-lg">
            <div class="modal-header">
              <DialogTitle v-if="contract.id" as="h3" class="modal-title">
                {{ t('createUpdateContractModal.contract_edit') }}
              </DialogTitle>
              <DialogTitle v-else as="h3" class="modal-title">
                {{ t('createUpdateContractModal.contract_create') }}
              </DialogTitle>
              <i class="btn-close cursor-pointer" @click="closeModalWith('cancel')" aria-label="Close"></i>
            </div>
            <form ref="form" v-on:submit.prevent="closeModalWith('confirm')">
              <div class="modal-body">
                <div class="row" v-if="contract?.meta?.is_trashed">
                  <div class="col-12">
                    <div class="alert alert-danger">{{ t('createUpdateContractModal.contract_is_deleted') }}</div>
                  </div>
                </div>
                <!-- klient už má aktivní smlouvu každého dostupného typu – není co založit -->
                <div class="row" v-if="noTypeAvailable">
                  <div class="col-12">
                    <div class="alert alert-info">{{ t('createUpdateContractModal.no_contract_type_available') }}</div>
                  </div>
                </div>
                <div class="row mb-3" v-else>
                  <div class="col-12 form-group">
                    <label for="contractTypeSelect" class="form-label">{{ t('createUpdateContractModal.type') }}</label>
                    <select v-model="contract.contract_type_id" name="contractTypeSelect" id="contractTypeSelect" class="form-control" required v-if="!editing">
                      <option v-for="type in contractTypes" :key="type.id" :value="type.id">{{ type.name }}</option>
                    </select>
                    <input v-if="editing" :value="contract?.contract_type?.name"  class="form-control" disabled />
                  </div>
                </div>
                <div v-if="editing" class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="contractNumberInput" class="form-label">{{ t('createUpdateContractModal.contract_number') }}</label>
                    <input v-model="contract.contract_number" :disabled="contract?.meta?.is_trashed" name="contractNumberInput" id="contractNumberInput" class="form-control" />
                  </div>
                </div>
              </div>

              <div class="modal-footer">
                <button class="btn btn-link text-decoration-underline text-dark" @click="closeModalWith('cancel')">{{ t('createUpdateContractModal.cancel') }}</button>
                <button v-if="!contract?.meta?.is_trashed && contract.id" class="btn btn-danger btn-md" @click.prevent="deleteContract">{{ t('createUpdateContractModal.delete') }}</button>
                <button v-if="!contract?.meta?.is_trashed && !noTypeAvailable" class="btn btn-primary btn-md" type="submit">{{ t('createUpdateContractModal.save') }}</button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

